import type { RailLayout } from "@/data/rail-branches";
import type { MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";
import { railCurveMs, recentLaneChangeMs } from "../RailLanes/lanes";
import { recentPlaceGroupChangeMs } from "../RailPlaceGroups/groups";
import { seatedOffset } from "../RailSeat/seat";
import { stayAt } from "../StayAt/stay";
import { recentVehicleChangeMs } from "../RailVehicleRow/row";

const SEAT_CHANGE_MS = 1_000;

export function easedSeat(
  characterId: string,
  playheadMs: number,
  rail: RailLayout,
): MarkerCircleOffset {
  const follow = (id: string, ms: number): MarkerCircleOffset => easedSeat(id, ms, rail);
  const now = seatedOffset(characterId, playheadMs, rail, follow) ?? { dx: 0, dy: 0 };
  const curveMs = railCurveMs();
  const laneChange = recentLaneChangeMs(rail.branches, playheadMs, curveMs);
  const stay = stayAt(rail.stays, characterId, playheadMs);
  const placeChange = laterMs(
    recentPlaceGroupChangeMs(rail.stays, playheadMs, SEAT_CHANGE_MS),
    stay?.vehicle === true
      ? recentVehicleChangeMs(rail.stays, stay.rootPlaceId, playheadMs, SEAT_CHANGE_MS)
      : undefined,
  );
  // A group changing as a rail joins settles once, at the group's pace. Otherwise the rail's
  // longer ease takes over when the group's ends, and throws everyone back toward their old seats.
  const groupChangedWithLane = laneChange !== undefined
    && recentPlaceGroupChangeMs(rail.stays, laneChange, 1) === laneChange;
  const changeAt = laterMs(laneChange, placeChange);
  if (changeAt === undefined) {
    return now;
  }
  const before = seatedOffset(characterId, changeAt - 1, rail, follow);
  if (before === null) {
    return now;
  }
  // Seats follow a fork along its curve; otherwise people settle fast, off each other's names.
  const easeMs = changeAt === placeChange || groupChangedWithLane ? SEAT_CHANGE_MS : curveMs;
  const t = smooth((playheadMs - changeAt) / easeMs);
  return {
    dx: lerp(before.dx, now.dx, t),
    dy: lerp(before.dy, now.dy, t),
  };
}

function laterMs(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) {
    return b;
  }
  if (b === undefined) {
    return a;
  }
  return Math.max(a, b);
}

export function smooth(t: number): number {
  const clamped = clamp01(t);
  return clamped * clamped * (3 - 2 * clamped);
}

export function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
